<template>
  <sc-steps
    :is-open="isOpen"
    :percentage="percentage"
    :title="title"
    :sub-title="SUB_TITLE"
    :description="DESCRIPTION"
  >
    <sc-button
      v-if="parentKey"
      slot="action-back"
      @click="onParentClick(null)"
      type="ghost-transparent"
      is-icon-only="true"
      icon-left="arrow-left"
      size="small"
    ></sc-button>
    <sc-button
      slot="action-close"
      @click="onToggle()"
      type="ghost-transparent"
      is-icon-only="true"
      icon-left="arrow-down"
      size="small"
    ></sc-button>
    <sc-steps-item
      v-for="(item, i) in enabledOnboardingItems.filter(item =>
        parentKey
          ? item.parentKey === parentKey
          : item.isParent || !item.parentKey,
      )"
      @click="item.onClick()"
      :is-parent="item.isParent"
      :is-active="getIsActive(item.key)"
      :count="getCount(item)"
      :done="getDoneCount(item)"
      :key="i"
      :name="item.name"
    ></sc-steps-item>
    <sc-steps-indicator
      @click="onToggle()"
      :is-open="isOpen"
      :is-completed="percentage === 100"
      :percentage="percentage"
      :title="TITLE"
      slot="indicator"
    ></sc-steps-indicator>
  </sc-steps>
</template>

<script>
import { mapGetters } from 'vuex';
import { DEFAULT_ONBOARDING_STATUS } from '@/constants/onboarding';

const BODY_ONBOARDING_CLASSNAME = 'body--onboarding';
const TITLE = 'My Onboarding';
const SUB_TITLE = 'Welcome to Soraban!';
const DESCRIPTION =
  'To ensure correct and convenient use we recommend following all the initial steps';

export default {
  data() {
    return {
      TITLE,
      SUB_TITLE,
      DESCRIPTION,
      BODY_ONBOARDING_CLASSNAME,
      isOpen: false,
      parentKey: null,
      activeKey: null,
    };
  },
  methods: {
    onToggle() {
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        return document.body.classList.add(BODY_ONBOARDING_CLASSNAME);
      }

      return document.body.classList.remove(BODY_ONBOARDING_CLASSNAME);
    },
    onParentClick(parentKey) {
      this.parentKey = parentKey;
    },
    onAddTeamMember() {
      this.$router.push('/firm-settings#onboarding-add-team-member');
      this.activeKey = 'has_multiple_firm_members';
    },
    onFirmEmailIntegration() {
      this.$router.push('/firm-settings#onboarding-firm-email-integration');
      this.activeKey = 'has_firm_email_integrated';
    },
    onContactSheetUpload() {
      this.$router.push('/firm-settings#onboarding-upload-contact-sheet');
      this.activeKey = 'has_contact_spreadsheet_uploaded';
    },
    onImportPriorYearData() {
      this.$router.push('/firm-settings#onboarding-import-rollover-data');
      this.activeKey = 'imported_prior_year_data';
    },
    onUpdateQuestionnaireQuestion() {
      this.$router.push('/questionnaire-templates');
      this.activeKey = 'updated_questionnaire_question';
    },
    onUpdateEngagementLetter() {
      this.$router.push('/questionnaire-templates');
      this.activeKey = 'updated_engagement_letter';
    },
    onUpdateQuestionnaireMessaging() {
      this.$router.push(
        '/questionnaire-templates#onboarding-customize-questionnaire-messages',
      );
      this.activeKey = 'updated_questionnaire_messaging';
    },
    onAddClient() {
      this.$router.push('/overview#onboarding-introduce-clients');
      this.activeKey = 'introduced_soraban_to_clients';
    },
    onSendQuestionnaires() {
      this.$router.push('/overview#onboarding-send-questionnaires');
      this.activeKey = 'has_sent_questionnaire';
    },
    getCount({ key, isParent }) {
      if (!isParent) return 1;

      const childrenItems = this.enabledOnboardingItems.filter(
        item => item.parentKey === key,
      );
      return childrenItems.length || 0;
    },
    getIsActive(key) {
      return this.activeKey === key;
    },
    getDoneCount(item) {
      const childrenItems = item.isParent
        ? this.enabledOnboardingItems.filter(oi => oi.parentKey === item.key)
        : [item];
      const doneCount = childrenItems.filter(
        ci => this.onboardingStatus[ci.key] === true,
      );

      return doneCount.length || 0;
    },
  },
  computed: {
    ...mapGetters({
      currentUser: 'user',
      defaultCompany: 'defaultCompany',
    }),
    isPartner() {
      return this.currentUser.firm_access === 'partner';
    },
    title() {
      const parentItem = this.enabledOnboardingItems.find(
        item => item.key === this.parentKey,
      );

      return parentItem ? parentItem.name : TITLE;
    },
    percentage() {
      const filteredOnboardingStatus = Object.keys(
        this.onboardingStatus,
      ).filter(key =>
        this.enabledOnboardingItems.find(item => item.key === key),
      );

      const count = Object.keys(filteredOnboardingStatus).length;
      const filled = Object.values(this.onboardingStatus).filter(Boolean)
        .length;

      return Math.round((filled / count) * 100);
    },
    onboardingItems() {
      return [
        {
          name: 'Key Setup Steps',
          key: 'all_key_steps',
          isParent: true,
          onClick: () => this.onParentClick('all_key_steps'),
        },
        {
          name: 'Add an additional team members to the account',
          key: 'has_multiple_firm_members',
          onClick: this.onAddTeamMember,
          parentKey: 'all_key_steps',
          isDisabled: !this.isPartner,
        },
        {
          name: 'Add a webhook',
          key: 'has_firm_email_integrated',
          onClick: this.onFirmEmailIntegration,
          parentKey: 'all_key_steps',
          isDisabled: !this.isPartner,
        },
        {
          name: 'Upload client contact spreadsheet',
          key: 'has_contact_spreadsheet_uploaded',
          onClick: this.onContactSheetUpload,
          parentKey: 'all_key_steps',
          isDisabled: true,
        },
        {
          name: 'Import rollover data',
          key: 'imported_prior_year_data',
          onClick: this.onImportPriorYearData,
          parentKey: 'all_key_steps',
          isDisabled: !this.isPartner,
        },
        {
          name: 'Onboarding Customizations',
          key: 'all_customizations',
          isParent: true,
          onClick: () => this.onParentClick('all_customizations'),
        },
        {
          name: 'Adjust questionnaire questions as necessary',
          key: 'updated_questionnaire_question',
          onClick: this.onUpdateQuestionnaireQuestion,
          parentKey: 'all_customizations',
        },
        {
          name: 'Revise engagement letters as required',
          key: 'updated_engagement_letter',
          onClick: this.onUpdateEngagementLetter,
          parentKey: 'all_customizations',
        },
        {
          name: 'Modify questionnaire messaging as needed',
          key: 'updated_questionnaire_messaging',
          onClick: this.onUpdateQuestionnaireMessaging,
          parentKey: 'all_customizations',
        },
        {
          name: 'Introduce Soraban To Your Clients',
          key: 'introduced_soraban_to_clients',
          onClick: this.onAddClient,
        },
        {
          name: "Let's Send Out Questionnaires",
          key: 'has_sent_questionnaire',
          onClick: this.onSendQuestionnaires,
        },
      ];
    },
    enabledOnboardingItems() {
      return this.onboardingItems
        .filter(item => {
          if (!item.isParent) return item;

          return this.onboardingItems.some(
            it => it.parentKey === item.key && !it.isDisabled,
          );
        })
        .filter(x => !x.isDisabled);
    },
  },
  props: {
    onboardingStatus: {
      type: Object,
      required: true,
      default: DEFAULT_ONBOARDING_STATUS,
    },
  },
};
</script>
